import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkle, LogoMark } from "@/pages/landing/primitives";

/**
 * Shared shell for /login, /signup, /forgot-password, /reset-password
 * and /verify-email. Keeps the auth surface visually consistent with the
 * landing page (same nav, Sparkle glyph, same off-white bg) while
 * centring the card and adding a soft brand-tinted glow behind it.
 */
export default function AuthShell({ title, subtitle, children, footer, testId = "auth-shell" }) {
  const navigate = useNavigate();
  useEffect(() => { document.title = `${title} · Interest To Income`; }, [title]);
  return (
    <div className="min-h-screen bg-[#FFFDF0] text-slate-900 flex flex-col" data-testid={testId}>
      <header className="border-b-2 border-black/10 bg-[#FFFDF0]/90 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="focus:outline-none" data-testid="auth-home-link">
            <LogoMark />
          </button>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 font-medium" data-testid="auth-back-home">
            ← Back to home
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 iti-sparkle mb-3">
              <Sparkle className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-widest text-purple-700">Interest to Income</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{subtitle}</p>}
          </div>
          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0_0_#111] p-6 sm:p-8">
            {children}
          </div>
          {footer && <div className="mt-6 text-center text-sm text-slate-600">{footer}</div>}
        </div>
      </main>
    </div>
  );
}
